import "antd/dist/antd.css";
import "../App.css";

import GuideArticle from "../components/GuideArticle";
import GuideSection from "../components/GuideSection";
import GuideStackLead from "../components/GuideStackLead";
import GuideCtaBand from "../components/GuideCtaBand";
import ChannelCrossingsMap from "../components/ChannelCrossingsMap";
import CtaCard from "../components/CtaCard";
import RelatedGuides from "../components/RelatedGuides";
import usePageMeta from "../usePageMeta";
import { GUIDES } from "../data/guides";

const META = GUIDES.find((g) => g.slug === "channel-crossings")!;

function ChannelCrossingGuide() {
    usePageMeta({
        title:
            "Getting your motorbike across the Channel — ferry vs Eurotunnel | Planamoto",
        description:
            "LeShuttle, Dover–Calais, or an overnight boat to Holland or Spain — how each crossing works with a motorbike, what strapping down actually looks like, when to book, and why the long ferries are cheaper than they look.",
        url: "https://www.planamoto.com/guides/channel-crossings",
        image: `https://www.planamoto.com${META.image!.src}`,
    });

    return (
        <GuideArticle
            eyebrow="Guide"
            title="Getting your motorbike across the Channel — ferry vs Eurotunnel"
            lede="The crossing is the first decision of every European tour, and it quietly shapes the whole trip — how far you ride on day one, where the first hotel is, and what the real cost of 'cheap' turns out to be. Here's how each option works with a bike, from the 35-minute tunnel to the 30-hour boat to Spain."
            heroImage={{
                src: META.image!.src,
                alt: META.image!.alt ?? "Cross-channel ferry passing the White Cliffs of Dover",
            }}
            articleSchema={{
                slug: META.slug,
                datePublished: META.datePublished,
                dateModified: META.dateModified,
                image: META.image?.src,
                description: META.summary,
            }}
        >
            <GuideStackLead
                eyebrow="The short version"
                authorNote="Spencer · every one of these ridden, strapped and slept on"
                heading="Three ways over, three different trips"
                intro="There's no single right crossing — there's the right crossing for where you're headed and where you start from. The tunnel buys time, Dover buys pennies, and the long boats buy you a night's sleep and several hundred miles you never have to ride."
                columns={[
                    {
                        label: "01 · LeShuttle",
                        title: "Fastest, driest, no straps",
                        body: "Folkestone to Calais in 35 minutes. Ride on, park on the side stand, stand beside the bike. No sea state, no strapping, no seasickness — and departures every hour or better.",
                    },
                    {
                        label: "02 · Dover–Calais",
                        title: "Cheapest way to France",
                        body: "Ninety minutes at sea with P&O, DFDS or Irish Ferries. Bikes usually board first and leave first. Your bike gets strapped to the deck; you get a coffee and a sit-down.",
                    },
                    {
                        label: "03 · The long crossings",
                        title: "Skip the miles, sleep aboard",
                        body: "Overnight to Rotterdam or Amsterdam for Germany and the Alps, or 24-plus hours to Santander or Bilbao to skip France entirely. The fare stings until you count the hotel nights and fuel it replaces.",
                    },
                ]}
                backstop="a flexible ticket. Bike plans move with the weather and the group, and the small extra for a flexible crossing is the cheapest insurance on the whole trip."
            />
            <div className="gt-guide-transition">
                <div className="gt-eyebrow gt-guide-transition__eyebrow">
                    The full picture
                </div>
                <h2 className="gt-guide-transition__heading">
                    Pick the crossing, trust the straps, book it right.
                </h2>
                <p className="gt-guide-transition__intro">
                    The five crossings worth comparing, what actually happens on the
                    vehicle deck, how the pricing behaves — and the maths that makes
                    a £400 ferry the cheap option.
                </p>
            </div>

            <ChannelCrossingsMap />

            <GuideSection
                index={1}
                heading="The five crossings that matter"
                intro="Fares below are ballpark returns for a bike and rider — high summer and weekends move all of them, and the long crossings include a cabin because you'll want one. Treat the numbers as a way to compare, not a quote."
                tools={[
                    {
                        name: "LeShuttle (Eurotunnel)",
                        cost: "≈ £120–£180 return · 35 min",
                        url: "https://www.leshuttle.com",
                        tagline: "Folkestone–Calais under the sea, bike ridden on and off.",
                        strengths:
                            "The fastest crossing by a distance — 35 minutes platform to platform, up to three or four departures an hour, and around an hour of check-in rather than a ferry terminal queue. You ride into the carriage, put the bike on its side stand in gear, and stay with it. No strapping, no swell, no seasick pillion. Runs in weather that cancels ferries.",
                        weaknesses:
                            "Usually the priciest of the short crossings, and the least romantic — you see concrete, then France. No café stop or leg-stretch; you're stood beside the bike in a bright aluminium tube. Peak-summer Fridays sell out and surge like airline seats.",
                        bestFor:
                            "Riders on a schedule, groups who want everyone across together with zero faff, and anyone whose pillion turns green on a swell.",
                    },
                    {
                        name: "Dover–Calais ferries",
                        cost: "≈ £80–£140 return · 90 min",
                        url: "https://www.poferries.com",
                        tagline: "P&O, DFDS and Irish Ferries — the classic cheap way over.",
                        strengths:
                            "Nearly always the cheapest crossing, with sailings roughly hourly between the three operators. Bikes typically board early and roll off first, so you're ahead of the car queue into France. Ninety minutes is enough for breakfast and a walk on deck — a genuinely nice start to a tour after a dark ride down to Dover.",
                        weaknesses:
                            "Check-in wants you there 60–90 minutes early, so door-to-door it's slower than the tunnel. The bike gets strapped on the vehicle deck — fine, but it's the bit newcomers fret about. Rough weather can delay or cancel, and you can't visit the bike mid-crossing.",
                        bestFor:
                            "Budget-first riders, relaxed schedules, and anyone who thinks a tour should start with a fry-up at sea rather than a tube under it.",
                    },
                    {
                        name: "Hull–Rotterdam (P&O)",
                        cost: "≈ £300–£450 return with cabin · 11 hrs overnight",
                        url: "https://www.poferries.com",
                        tagline: "Board in the evening, wake up in Holland.",
                        strengths:
                            "The northern rider's cheat code. Board around dinner time, eat, sleep in a cabin, and ride off at Europoort in the morning — a day's motorway slog to Dover and a night in a hotel both quietly deleted. Puts you an easy morning's ride from Germany, and a day from the Alps or the Mosel.",
                        weaknesses:
                            "The fare looks steep next to Dover until you do the maths (see below). One sailing a day, so a missed boat is a lost day. The North Sea can roll enough in autumn and winter to make the strap-down worth double-checking.",
                        bestFor:
                            "Anyone north of the Midlands headed for Germany, the Alps or eastern Europe — the miles you skip are the boring ones.",
                    },
                    {
                        name: "Newcastle–Amsterdam (DFDS)",
                        cost: "≈ £300–£500 return with cabin · 16 hrs overnight",
                        url: "https://www.dfds.com",
                        tagline: "The same overnight trick, further north still.",
                        strengths:
                            "Same argument as Hull with an even longer ride avoided for riders in the North East and Scotland. Sails late afternoon, arrives IJmuiden mid-morning; dinner, a pint, a cabin, and Holland with the whole day ahead. The extra hours at sea are hours you'd otherwise spend on the A1.",
                        weaknesses:
                            "The longest of the North Sea runs, and mid-morning arrival means day one's riding starts later than Hull's. Cabin and sailings pricing climbs sharply in school holidays.",
                        bestFor:
                            "Riders from the North East and Scotland — and anyone who'd rather start the tour rested than prove something on English motorways.",
                    },
                    {
                        name: "Portsmouth–Santander / Bilbao (Brittany Ferries)",
                        cost: "≈ £400–£700 return with cabin · 24–32 hrs",
                        url: "https://www.brittany-ferries.co.uk",
                        tagline: "Skip France entirely — wake up in northern Spain.",
                        strengths:
                            "Deletes roughly 700 miles of France each way: no péages, no French fuel bill, no two transit days and hotel nights in each direction. You sail across Biscay, watch for whales off the deck, and ride down the ramp into the Picos de Europa — some of the best biking roads in Europe start within an hour of the port. For Spain and Portugal it transforms the trip.",
                        weaknesses:
                            "The headline fare frightens people, and summer cabins sell out months ahead. Biscay is a real sea — most crossings are fine, but a rough one is memorable, and it's the crossing where the strap-down genuinely matters. Two sailings a week on some routes, so build the timetable around it.",
                        bestFor:
                            "Spain and Portugal tours, riders short on annual leave, and anyone doing the maths on what a 'cheap' Dover ticket really costs by Santander.",
                    },
                ]}
            />

            <GuideSection
                index={2}
                columns={[
                    {
                        heading: "The vehicle-deck drill",
                        items: [
                            {
                                marker: "1",
                                title: "Follow the loader, bikes first",
                                body: "On most ferries bikes board early and park in their own row along the deck. Follow the crew's hand signals — they park bikes for a living and they're putting you where the straps are.",
                            },
                            {
                                marker: "2",
                                title: "Side stand, in gear, steering unlocked",
                                body: "Park on the side stand — not the centre stand — and leave it in first gear. The side stand lets the bike lean into the strap and flex with the ship. Leave the steering unlocked in case the crew need to shift it.",
                            },
                            {
                                marker: "3",
                                title: "Strap over the seat, pulled down",
                                body: "The standard tie is one strap from deck ring to deck ring over the seat, tensioned so the bike is pressed down onto its suspension and side stand. On the short crossings the crew provide the straps and usually fit them; do it yourself if invited, and check it either way.",
                            },
                            {
                                marker: "4",
                                title: "Protect the soft bits",
                                body: "A rag, an old towel or a scrap of foam under the strap saves the seat cover and any luggage it crosses. Worth carrying just for this — it weighs nothing and lives in a pannier lid.",
                            },
                            {
                                marker: "5",
                                title: "Take what you need up top",
                                body: "The vehicle deck is sealed once you sail — there's no popping back for a jumper. Helmet, valuables, jacket, and a small bag for the crossing come with you; on the overnight boats pack a separate cabin bag so you're not unstrapping panniers on the deck.",
                            },
                        ],
                    },
                    {
                        heading: "Should you trust the ferry's straps?",
                        items: [
                            {
                                marker: "✓",
                                title: "On the short crossings — yes",
                                body: "Dover–Calais in normal weather barely moves, and the crew's over-the-seat tie has held countless bikes through worse than your crossing. Check the tension yourself before you walk away, and that's genuinely enough.",
                            },
                            {
                                marker: "✓",
                                title: "On Biscay — strap like you mean it",
                                body: "A 30-hour crossing can meet real weather. Take the crew's strap and add your own: a couple of soft loops and a cam or ratchet strap weigh a few hundred grams and turn 'probably fine' into 'not thinking about it at dinner'.",
                            },
                            {
                                marker: "✓",
                                title: "The check before you leave the deck",
                                body: "Push the bike gently against the strap — it should rock the suspension, not shuffle its wheels. Strap bearing on paint or cables? Re-route it. Then take a photo: parked row and deck number, and you'll find it fast among four hundred vehicles at the other end.",
                            },
                        ],
                    },
                ]}
            />

            <GuideSection
                index={3}
                heading="Booking — timing and the price patterns"
                intro="Crossings price like airline seats: demand moves them daily, and the patterns are predictable enough to use."
                items={[
                    {
                        title: "Book early for summer, gamble in the shoulder",
                        body: "July and August weekends — and any bank holiday — sell up and out weeks ahead on every route. Book those as soon as the dates are fixed. May, June and September midweek you can often book days ahead and pay less than the summer crowd.",
                    },
                    {
                        title: "Antisocial hours are where the savings live",
                        body: "Dawn and late-evening departures on the tunnel and Dover boats run reliably cheaper than the 10am-to-4pm sweet spot. On a bike tour an early crossing is a feature anyway — you're in France for breakfast with the whole day ahead.",
                    },
                    {
                        title: "The Spain boats sell out first",
                        body: "Cabins on Portsmouth–Santander and Bilbao are the scarce resource of the whole system — school-holiday sailings can be gone months ahead. If the trip depends on that crossing, book the ferry before the hotels, not after.",
                    },
                    {
                        title: "Pay the little extra for flexible",
                        body: "Weather, a group member's clutch cable, one more night in the mountains — bike tours drift. Flexible tickets on the tunnel will put you on an earlier or later train without drama, and amendable ferry fares cost far less than rebooking a fixed one.",
                    },
                    {
                        title: "Compare on the aggregators, book direct",
                        body: "Comparison sites are good for seeing every route and operator on one screen. Once you've picked, booking with the operator makes changes, cancellations and turn-up-early requests far simpler — worth more than a small aggregator discount the week something goes wrong.",
                    },
                    {
                        title: "Returns beat singles",
                        body: "On almost every route a return booked in one go undercuts two singles — and it fixes the going-home decision while you can still get the sailing you want.",
                    },
                ]}
            />

            <GuideSection
                index={4}
                heading="The overnight ferry is a hotel night — do the maths"
                intro="The long crossings only look expensive while you're comparing fares. Compare trips instead — the fare buys distance, a bed and a rested first day, and the calculator makes the comparison honest:"
                highlight={{
                    glyph: "★",
                    background: "var(--gt-accent)",
                    color: "#3a2a0a",
                }}
                items={[
                    {
                        title: "What the Dover ticket really costs by Spain",
                        body: "Ride to southern Spain via Calais and you'll spend roughly two days each way in transit across France: two or more hotel nights, four tanks of fuel, a run of péages and every meal in between — typically £250–£350 on top of the cheap crossing, plus four days of annual leave spent on motorways.",
                    },
                    {
                        title: "What the Santander fare buys back",
                        body: "The £400–£700 return replaces most of that spend and hands the transit days back as riding days in the Picos. Fare minus the hotels, fuel, tolls and meals it deletes, and the 'expensive' boat lands within pennies of the 'cheap' route — while arriving fresh instead of motorway-numb.",
                    },
                    {
                        title: "The same sum works on the North Sea",
                        body: "Hull or Newcastle to Holland replaces a full day's ride to Dover, a night in a hotel and the fuel between — from the north of England the overnight boat is frequently the cheaper option outright, before you even price your own time.",
                    },
                    {
                        title: "Run your own numbers, not mine",
                        body: "Days, accommodation style, distance and the Channel crossing are all inputs in our trip cost calculator — set the trip up both ways and let the totals argue it out for your route, your dates and your bike.",
                    },
                ]}
            />

            <GuideCtaBand
                heading="Price the crossing inside the whole trip."
                body="The cost calculator has a Channel-crossing setting alongside days, hotels, distance and meals — flip it between the tunnel, Dover and the long boats and watch what the trip actually costs each way."
                primary={{ label: "Try the cost calculator", to: "/guides/europe-trip-cost" }}
            />

            <RelatedGuides
                slugs={[
                    "europe-trip-cost",
                    "packing-europe",
                    "french-toll-roads",
                    "fuel-prices",
                    "bike-security",
                ]}
            />

            <CtaCard
                title="Booked the boat? Plan the rest"
                body="Ports and check-in times drop straight into a Planamoto itinerary alongside the stops, overnight addresses and shared expenses — so the whole group knows where the ferry is and when it sails. Browse a curated tour to see a trip laid out, or start your own."
                primary={{ label: "Browse tours", to: "/explore" }}
            />
        </GuideArticle>
    );
}

export default ChannelCrossingGuide;
